import React, { createRef, useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  InputGroup,
  ListGroup,
  Modal,
  Row,
} from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import {
  DrawingFileModel,
  DrawingFileTypeModel,
  LocaleModel,
  Locales,
  drawingApi,
} from "../api/drawingApi";
import { Binoculars, Trash, Upload } from "react-bootstrap-icons";

interface PdfLanguagesModalProps {
  vehicleFileNumber: string;
  readOnly: boolean;
  onClose: () => void;
}

function PdfLanguagesModal({
  vehicleFileNumber,
  readOnly,
  onClose,
}: PdfLanguagesModalProps) {
  const [error, setError] = useState<Problem | string | undefined>();
  const [pdfs, setPdfs] = useState<DrawingFileModel[]>();
  const [selectedPdf, setSelectedPdf] = useState<DrawingFileModel>();
  const [selectedLocale, setSelectedLocale] = useState<LocaleModel>();
  const fileInputRef = createRef<HTMLInputElement>();

  const handleClose = () => {
    setError(undefined);
    onClose();
  };

  useEffect(() => {
    async function getPdfs() {
      const result = await drawingApi.getDrawingFiles(
        vehicleFileNumber,
        DrawingFileTypeModel.PDF,
      );

      if (result.ok) {
        setError(undefined);
        setPdfs(result.value);
      } else {
        setError(result.problem);
      }
    }

    getPdfs();
  }, [vehicleFileNumber]);

  const pdfIncludesLocale = (
    pdf: DrawingFileModel,
    locale: LocaleModel,
  ): boolean => {
    return pdf.locales.includes(locale);
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length !== 1 || !selectedLocale || !selectedPdf) return;

    const result = await drawingApi.uploadDrawingFile(
      vehicleFileNumber,
      files[0],
      selectedPdf.name,
      selectedLocale,
      DrawingFileTypeModel.PDF,
    );

    if (result.ok) {
      if (result.value !== selectedPdf.name) {
        setError(`PDF file already exists with the name '${result.value}'.`);

        setSelectedPdf(pdfs?.find((df) => df.name === result.value));
        return;
      } else {
        setError(undefined);
      }

      if (!selectedPdf?.locales.includes(selectedLocale)) {
        const modifiedPdf = {
          ...selectedPdf,
          locales: [...selectedPdf.locales, selectedLocale],
        };
        const modifiedPdfs = pdfs?.map((p) =>
          p === selectedPdf ? modifiedPdf : p,
        );

        setPdfs(modifiedPdfs);
        setSelectedPdf(modifiedPdf);
      }
    } else {
      setError(result.problem);
    }
  };

  const handleView = (pdf: DrawingFileModel, locale: LocaleModel) => {};

  const handleRemove = async (pdf: DrawingFileModel, locale: LocaleModel) => {
    if (!window.confirm("Are you sure?")) return;

    const result = await drawingApi.removeDrawingFile(
      vehicleFileNumber,
      pdf.name,
      DrawingFileTypeModel.PDF,
      locale,
    );

    if (result.ok) {
      setError(undefined);

      const modifiedPdf = {
        ...pdf,
        locales: pdf.locales.filter((l) => l !== locale),
      };

      const modifiedPdfs = pdfs?.map((p) => (p === pdf ? modifiedPdf : p));

      setPdfs(modifiedPdfs);
      setSelectedPdf(modifiedPdf);
    } else {
      setError(result.problem);
    }
  };

  return (
    <Modal show={true} onHide={() => handleClose()} size="xl">
      <Modal.Header>
        <Modal.Title>Edit PDF languages</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <ErrorDisplay error={error} />
          <Row>
            <Col>
              {!pdfs && "loading..."}
              {pdfs &&
                pdfs.length === 0 &&
                "no pdfs available in the drawing..."}
              {pdfs && pdfs.length > 0 && (
                <ListGroup>
                  {pdfs.map((p) => (
                    <ListGroup.Item
                      key={p.name}
                      className="text-nowrap"
                      action
                      onClick={() => setSelectedPdf(p)}
                      active={selectedPdf === p}
                    >
                      {p.name}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Col>
            <Col>
              {selectedPdf && (
                <>
                  <Row>
                    <InputGroup className="mb-1">
                      <InputGroup.Text>
                        {LocaleModel[LocaleModel.English]}
                      </InputGroup.Text>
                      <Button
                        onClick={() =>
                          handleView(selectedPdf, LocaleModel.English)
                        }
                      >
                        <Binoculars />
                      </Button>
                      <Button
                        onClick={() =>
                          handleRemove(selectedPdf, LocaleModel.English)
                        }
                      >
                        <Trash />
                      </Button>
                    </InputGroup>
                  </Row>
                  <Row className="gy-2">
                    {Locales.filter((l) => l !== LocaleModel.English).map(
                      (l) => (
                        <Col key={l} md="auto">
                          <InputGroup size="sm">
                            <InputGroup.Text style={{ width: "100px" }}>
                              {LocaleModel[l]}
                            </InputGroup.Text>
                            <Button
                              onClick={() => handleView(selectedPdf, l)}
                              disabled={!pdfIncludesLocale(selectedPdf, l)}
                            >
                              <Binoculars />
                            </Button>
                            <Button disabled={readOnly}>
                              <Upload
                                title="Upload"
                                onClick={() => {
                                  setSelectedLocale(l);
                                  fileInputRef.current?.click();
                                }}
                              />
                            </Button>
                            <Button
                              disabled={
                                readOnly || !pdfIncludesLocale(selectedPdf, l)
                              }
                              onClick={() => handleRemove(selectedPdf, l)}
                            >
                              <Trash />
                            </Button>
                          </InputGroup>
                        </Col>
                      ),
                    )}
                  </Row>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    style={{ display: "none" }}
                    onChange={(e) => handleUpload(e.target.files)}
                  />
                </>
              )}
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
}

export default PdfLanguagesModal;
